import { useLayoutEffect, useRef, useState } from "react";

export type TargetConfigHintRow = { label: string; value: string };

export type TargetConfigHintData = {
  title: string;
  subtitle: string;
  rows: TargetConfigHintRow[];
  layers: TargetConfigHintRow[];
  finalModels: string;
  finalModelsSource: string;
  finalMapping: string;
  finalMappingSource: string;
  warning?: string;
  note?: string;
};

export function TargetConfigHint({
  data,
  anchor,
  onMouseEnter,
  onMouseLeave,
}: {
  data: TargetConfigHintData | null;
  anchor: DOMRect | null;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) {
  const margin = 8;
  const gap = 12;
  const [viewport, setViewport] = useState(() => ({
    width: typeof window === "undefined" ? 0 : window.innerWidth,
    height: typeof window === "undefined" ? 0 : window.innerHeight,
  }));
  const tipRef = useRef<HTMLDivElement | null>(null);
  const [tipHeight, setTipHeight] = useState(0);
  const [overflowing, setOverflowing] = useState(false);

  useLayoutEffect(() => {
    function update() {
      setViewport((prev) => {
        const next = { width: window.innerWidth, height: window.innerHeight };
        return prev.width === next.width && prev.height === next.height ? prev : next;
      });
    }
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const maxWidth = Math.max(0, Math.min(680, viewport.width - margin * 2));
  const minWidth = Math.min(480, maxWidth);
  const leftSpace = anchor ? Math.max(0, anchor.left - gap - margin) : 0;
  const rightSpace = anchor ? Math.max(0, viewport.width - anchor.right - gap - margin) : 0;
  const placeLeft = leftSpace >= rightSpace;
  const width = Math.max(0, Math.min(maxWidth, Math.max(minWidth, Math.max(leftSpace, rightSpace))));
  const maxHeight = Math.max(0, viewport.height - margin * 2);

  useLayoutEffect(() => {
    const el = tipRef.current;
    if (!el) {
      setTipHeight(0);
      setOverflowing(false);
      return;
    }
    function update() {
      if (!el) return;
      setTipHeight(el.getBoundingClientRect().height);
      setOverflowing(el.scrollHeight > el.clientHeight + 1);
    }
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [data, width, viewport.width, viewport.height]);

  if (!data || !anchor || viewport.width <= 0 || viewport.height <= 0) return null;

  // 优先放目标左侧，避免遮住下发目标；空间不足时改用右侧或收窄宽度。
  const preferredLeft = placeLeft ? anchor.left - width - gap : anchor.right + gap;
  const left = Math.min(Math.max(margin, preferredLeft), Math.max(margin, viewport.width - width - margin));

  // 用实测高度夹紧，长 URL / models / mapping / 说明不会超出视口底部。
  const visibleHeight = Math.min(tipHeight || 320, maxHeight);
  const maxTop = Math.max(margin, viewport.height - visibleHeight - margin);
  const top = Math.min(Math.max(margin, anchor.top), maxTop);

  return (
    <div
      ref={tipRef}
      role="tooltip"
      className="pointer-events-none fixed z-[70] overflow-hidden rounded-xl border border-border bg-card p-3 text-card-foreground shadow-2xl ring-1 ring-black/10"
      style={{
        left,
        top,
        width,
        margin: 0,
        maxHeight,
        overflowY: overflowing ? "auto" : "hidden",
        pointerEvents: overflowing ? "auto" : "none",
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="-mx-3 -mt-3 mb-2 border-b border-border bg-secondary px-3 py-2">
        <div className="break-words text-sm font-semibold">{data.title}</div>
        <div className="break-all text-[11px] text-muted-foreground">{data.subtitle}</div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <section className="space-y-1">
          <div className="mb-1 text-[11px] font-semibold text-foreground">目标参数</div>
          {data.rows.map((row) => (
            <div key={row.label} className="flex gap-2 text-[11px] leading-4">
              <span className="w-24 shrink-0 text-foreground/60">{row.label}</span>
              <span className={`min-w-0 flex-1 break-all ${row.value ? "text-foreground" : "text-muted-foreground"}`}>
                {row.value || "—"}
              </span>
            </div>
          ))}
        </section>

        <section className="space-y-1 sm:border-l sm:border-border sm:pl-4">
          <div className="mb-1 text-[11px] font-semibold text-foreground">模型链路（绑定 &gt; 站点 &gt; 分发 &gt; 模板）</div>
          {data.layers.map((layer) => (
            <div key={layer.label} className="flex gap-2 text-[11px] leading-4">
              <span className="w-24 shrink-0 text-foreground/60">{layer.label}</span>
              <span className={`min-w-0 flex-1 break-all ${layer.value ? "text-foreground" : "text-muted-foreground"}`}>
                {layer.value || "—"}
              </span>
            </div>
          ))}
          <div className="mt-2 rounded-md border border-border bg-secondary px-2.5 py-2 text-[11px] leading-4">
            <div className="flex gap-2">
              <span className="w-20 shrink-0 text-foreground/60">最终 Models</span>
              <span className={`min-w-0 flex-1 break-all font-mono font-medium ${data.finalModels ? "text-foreground" : "text-muted-foreground"}`}>
                {data.finalModels || "—"}
              </span>
            </div>
            <div className="flex gap-2">
              <span className="w-20 shrink-0 text-foreground/60">来源</span>
              <span className="font-medium text-primary">{data.finalModelsSource}</span>
            </div>
            <div className="mt-0.5 flex gap-2">
              <span className="w-20 shrink-0 text-foreground/60">Mapping</span>
              <span className={`min-w-0 flex-1 break-all font-mono font-medium ${data.finalMapping ? "text-foreground" : "text-muted-foreground"}`}>
                {data.finalMapping || "—"}
              </span>
            </div>
            <div className="flex gap-2">
              <span className="w-20 shrink-0 text-foreground/60">来源</span>
              <span className="font-medium text-primary">{data.finalMappingSource}</span>
            </div>
          </div>
        </section>
      </div>

      {data.warning && <div className="mt-2 rounded-md bg-amber-50 px-2 py-1 text-[11px] leading-4 text-amber-700">{data.warning}</div>}
      {data.note && <div className="mt-1.5 text-[11px] leading-4 text-foreground/70">{data.note}</div>}
    </div>
  );
}
